import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import FileUpload from './FileUpload';
import GalleryUpload from './GalleryUpload';
import { updatePost, getCategories } from '../services/api';

const PostEditModal = ({ post, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name_or_title: '',
    context: '',
    short_description: '',
    featured_image: null,
    gallery: [],
    gallery_style: '1-column', // Default: 1 column
    content_category: '',
    ad_title: '',
    ad_type: '',
    ad_url: '',
    ad_email: '',
    ad_phone: '',
    ad_date: '',
    comment_status: 'closed'
  });
  
  // Toggle states for UI
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [resetTrigger, setResetTrigger] = useState(0);
  const [existingFeaturedImage, setExistingFeaturedImage] = useState(null);
  const [existingGallery, setExistingGallery] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [copyNotification, setCopyNotification] = useState('');
  const [existingShortcodes, setExistingShortcodes] = useState([]);
  const [shortcodeCopied, setShortcodeCopied] = useState(false);

  // Function to detect gallery shortcodes in content
  const detectShortcodes = (content) => {
    const shortcodeRegex = /\[gallery[^\]]*\]/g;
    const matches = content.match(shortcodeRegex) || [];
    return matches.map((shortcode, index) => ({
      id: `shortcode-${index}`,
      text: shortcode,
      isPlaceholder: shortcode.includes('GALLERY_PLACEHOLDER')
    }));
  };

  // Function to remove a specific shortcode from content
  const removeShortcode = (shortcodeToRemove) => {
    const updatedContent = formData.context.replace(shortcodeToRemove, '');
    setFormData(prev => ({
      ...prev,
      context: updatedContent
    }));
    
    // Reset copied state when manually removing shortcodes
    setShortcodeCopied(false);
    
    setCopyNotification('Shortcode removed!');
    setTimeout(() => setCopyNotification(''), 2000);
  };

  // Check if form has unsaved changes
  const checkForChanges = () => {
    if (!post) return false;
    
    // Don't check for changes during submission or upload
    if (isSubmitting || uploadProgress > 0) {
      return false;
    }
    
    const hasChanges = 
      formData.name_or_title !== (post.name_or_title || '') ||
      formData.context !== (post.context || '') ||
      formData.short_description !== (post.short_description || '') ||
      formData.content_category !== (post.content_category?.id || '') ||
      formData.featured_image !== null ||
      (formData.gallery && formData.gallery.length > 0) ||
      existingFeaturedImage !== (post.featured_image || null) ||
      existingGallery.length !== (post.gallery?.length || 0) ||
      formData.ad_title !== (post.ad_title || '') ||
      formData.ad_type !== (post.ad_type || '') ||
      formData.ad_url !== (post.ad_url || '') ||
      formData.ad_email !== (post.ad_email || '') ||
      formData.ad_phone !== (post.ad_phone || '') ||
      formData.ad_date !== (post.ad_date || '');
    
    setHasUnsavedChanges(hasChanges);
    return hasChanges;
  };

  // Handle beforeunload event - DISABLED to prevent browser warnings
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Completely disabled - no browser warnings
      return;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Check for changes whenever form data changes
  useEffect(() => {
    checkForChanges();
  }, [formData, existingFeaturedImage, existingGallery, post]);

  // Detect shortcodes whenever content changes
  useEffect(() => {
    const shortcodes = detectShortcodes(formData.context);
    setExistingShortcodes(shortcodes);
  }, [formData.context]);

  // Handle close with confirmation
  const handleClose = () => {
    if (checkForChanges()) {
      setPendingAction('close');
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  // Handle confirmation modal
  const handleConfirmModal = () => {
    if (pendingAction === 'close') {
      onClose();
    }
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  // Handle cancel modal
  const handleCancelModal = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  useEffect(() => {
    if (post) {
      
      setFormData({
        name_or_title: post.name_or_title || '',
        context: post.context || '',
        short_description: post.short_description || '',
        featured_image: null, // Will be handled separately
        gallery: [], // Will be handled separately
        gallery_style: post.gallery_style || '1-column',
        content_category: post.content_category?.id || '',
        ad_title: post.ad_title || '',
        ad_type: post.ad_type || '',
        ad_url: post.ad_url || '',
        ad_email: post.ad_email || '',
        ad_phone: post.ad_phone || '',
        ad_date: post.ad_date || '',
        comment_status: post.comment_status || 'closed'
      });
      
      // Show additional fields if any exist
      setShowAdditionalFields(!!(post.ad_title || post.ad_type || post.ad_url || post.ad_email || post.ad_phone || post.ad_date));
      
      // Set existing images
      setExistingFeaturedImage(post.featured_image || null);
      setExistingGallery(post.gallery || []);
      
    }
  }, [post]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const categoriesData = await getCategories();
        setCategories(categoriesData);
      } catch (error) {
        setCategories([
          { id: 1, name: 'Uncategorized', slug: 'uncategorized' },
          { id: 33, name: 'Creative', slug: 'creative' },
          { id: 40, name: 'Design', slug: 'design' }
        ]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFeaturedImageChange = (file) => {
    setFormData(prev => ({
      ...prev,
      featured_image: file
    }));
    // Clear existing featured image when new one is selected
    setExistingFeaturedImage(null);
  };

  const handleGalleryChange = (files) => {
    setFormData(prev => ({
      ...prev,
      gallery: files
    }));
    
    // Don't clear existing gallery - new files are additional to existing ones
    // The existing gallery will be preserved and combined with new files during save
  };


  // Handle removing existing gallery images
  const handleRemoveExistingGalleryImage = (index) => {
    const newGallery = existingGallery.filter((_, i) => i !== index);
    setExistingGallery(newGallery);
    
    // Don't clear formData gallery - they are separate
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name_or_title.trim()) {
      newErrors.name_or_title = 'Title is required';
    }
    
    if (!formData.context.trim()) {
      newErrors.context = 'Content is required';
    }
    
    if (!formData.short_description.trim()) {
      newErrors.short_description = 'Short description is required';
    }
    
    if (!formData.content_category) {
      newErrors.content_category = 'Category is required';
    }

    setErrors(newErrors);
    
    // Focus on first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      focusOnField(firstErrorField);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const focusOnField = (fieldName) => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      let element = null;
      
      switch (fieldName) {
        case 'name_or_title':
          element = document.getElementById('name_or_title');
          break;
        case 'context':
          // For ReactQuill, we need to focus on the editor
          const quillEditor = document.querySelector('.ql-editor');
          if (quillEditor) {
            quillEditor.focus();
            quillEditor.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Add highlight effect to the Quill container
            const quillContainer = document.querySelector('.ql-container');
            if (quillContainer) {
              quillContainer.style.transition = 'box-shadow 0.3s ease';
              quillContainer.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.3)';
              
              setTimeout(() => {
                quillContainer.style.boxShadow = '';
              }, 2000);
            }
            return;
          }
          break;
        case 'short_description':
          element = document.getElementById('short_description');
          break;
        case 'content_category':
          element = document.getElementById('content_category');
          break;
        default:
          element = document.getElementById(fieldName);
      }
      
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Add a subtle highlight effect to draw attention
        element.style.transition = 'box-shadow 0.3s ease';
        element.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.3)';
        
        // Remove the highlight after 2 seconds
        setTimeout(() => {
          element.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setUploadProgress(0);
      setUploadMessage('');


      const result = await updatePost(post.id, formData, {
        featured_image: existingFeaturedImage,
        gallery: existingGallery
      }, (progress, message) => {
        setUploadProgress(progress);
        setUploadMessage(message);
      });

      
      setSuccessMessage('Post updated successfully!');
      
      // Close modal after 3 seconds (modal will be visible for 3 seconds)
      setTimeout(() => {
        // Call onSuccess callback to refresh parent component data
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      }, 3000);
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
      setUploadMessage('');
    }
  };

  if (!post) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Edit Post</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>


          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title Field */}
            <div>
              <label htmlFor="name_or_title" className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                id="name_or_title"
                value={formData.name_or_title}
                onChange={(e) => handleInputChange('name_or_title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.name_or_title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter post title"
              />
              {errors.name_or_title && (
                <p className="mt-1 text-sm text-red-600">{errors.name_or_title}</p>
              )}
            </div>

            {/* Short Description Field */}
            <div>
              <label htmlFor="short_description" className="block text-sm font-medium text-gray-700 mb-2">
                Short Description *
              </label>
              <textarea
                id="short_description"
                value={formData.short_description}
                onChange={(e) => handleInputChange('short_description', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.short_description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter a short description"
              />
              {errors.short_description && (
                <p className="mt-1 text-sm text-red-600">{errors.short_description}</p>
              )}
            </div>

            {/* Content Field */}
            <div>
              <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-2">
                Content *
              </label>
              <ReactQuill
                value={formData.context}
                onChange={(value) => handleInputChange('context', value)}
                className={`${errors.context ? 'border-red-300' : 'border-gray-300'}`}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['link', 'image'],
                    ['clean']
                  ],
                }}
                formats={[
                  'header', 'bold', 'italic', 'underline', 'strike',
                  'list', 'bullet', 'link', 'image'
                ]}
              />
              {errors.context && (
                <p className="mt-1 text-sm text-red-600">{errors.context}</p>
              )}
            </div>

            {/* Category Field */}
            <div>
              <label htmlFor="content_category" className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                id="content_category"
                value={formData.content_category}
                onChange={(e) => handleInputChange('content_category', e.target.value)}
                disabled={loadingCategories}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  errors.content_category ? 'border-red-300' : 'border-gray-300'
                } ${loadingCategories ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">
                  {loadingCategories ? 'Loading categories...' : 'Select a category'}
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.count} posts)
                  </option>
                ))}
              </select>
              {errors.content_category && (
                <p className="mt-1 text-sm text-red-600">{errors.content_category}</p>
              )}
            </div>

            {/* Featured Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image
              </label>
              
              {/* Existing Featured Image */}
              {existingFeaturedImage && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Current featured image:</p>
                  <div className="relative inline-block">
                    <img
                      src={existingFeaturedImage.url}
                      alt={existingFeaturedImage.alt || 'Current featured image'}
                      className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setExistingFeaturedImage(null);
                        // Clear any selected file as well
                        setFormData(prev => ({
                          ...prev,
                          featured_image: null
                        }));
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs"
                      title="Remove current image"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              
              {/* New Featured Image Preview */}
              {formData.featured_image && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">New featured image:</p>
                  <div className="relative inline-block">
                    <img
                      src={URL.createObjectURL(formData.featured_image)}
                      alt="New featured image"
                      className="w-32 h-32 object-cover rounded-lg border-2 border-green-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          featured_image: null
                        }));
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs"
                      title="Remove new image"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              <FileUpload
                onFileSelect={handleFeaturedImageChange}
                accept="image/*"
                maxSize={5 * 1024 * 1024} // 5MB
                label={existingFeaturedImage ? "Replace featured image" : "Choose featured image"}
              />
            </div>

            {/* Gallery Upload */}
            <div>
              {/* Gallery Style Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Gallery Layout
                </label>
                <div className="grid grid-cols-3 gap-4">
                    {/* 1 Column Option */}
                    <div 
                      className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.gallery_style === '1-column'
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleInputChange('gallery_style', '1-column')}
                    >
                      <input
                        type="radio"
                        name="gallery_style_edit"
                        value="1-column"
                        checked={formData.gallery_style === '1-column'}
                        onChange={(e) => handleInputChange('gallery_style', e.target.value)}
                        className="sr-only"
                      />
                      <div className="w-12 h-8 mb-2 flex flex-col gap-1">
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                      </div>
                        <span className="text-sm font-medium text-gray-700">1 Column</span>
                      </div>

                    {/* 2 Column Option */}
                    <div 
                      className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.gallery_style === '2-column'
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleInputChange('gallery_style', '2-column')}
                    >
                      <input
                        type="radio"
                        name="gallery_style_edit"
                        value="2-column"
                        checked={formData.gallery_style === '2-column'}
                        onChange={(e) => handleInputChange('gallery_style', e.target.value)}
                        className="sr-only"
                      />
                      <div className="w-12 h-8 mb-2 grid grid-cols-2 gap-1">
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                      </div>
                        <span className="text-sm font-medium text-gray-700">2 Column Grid</span>
                      </div>

                    {/* 3 Column Option */}
                    <div 
                      className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.gallery_style === '3-column'
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleInputChange('gallery_style', '3-column')}
                    >
                      <input
                        type="radio"
                        name="gallery_style_edit"
                        value="3-column"
                        checked={formData.gallery_style === '3-column'}
                        onChange={(e) => handleInputChange('gallery_style', e.target.value)}
                        className="sr-only"
                      />
                      <div className="w-12 h-8 mb-2 grid grid-cols-3 gap-1">
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                      </div>
                        <span className="text-sm font-medium text-gray-700">3 Column Grid</span>
                      </div>
                </div>
              </div>
              
              {/* Existing Gallery Images */}
              {existingGallery && existingGallery.length > 0 ? (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Current gallery images ({existingGallery.length}):</p>
                  <div className="flex flex-wrap gap-4">
                    {existingGallery.map((image, index) => {
                      return (
                        <div key={`existing-${image.id || index}`} className="relative group">
                          <img
                            src={image.url}
                            alt={image.alt || `Gallery image ${index + 1}`}
                            className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveExistingGalleryImage(index)}
                            className="absolute bottom-1 right-1 bg-red-500 hover:bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove image"
                          >
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <p className="text-sm text-gray-500">No existing gallery images found</p>
                </div>
              )}
              

              <GalleryUpload
                onFilesSelect={handleGalleryChange}
                accept="image/*"
                maxSize={5 * 1024 * 1024} // 5MB
                maxFiles={10}
                label={existingGallery.length > 0 ? "Add more gallery images" : "Choose gallery images"}
                resetTrigger={resetTrigger}
              />
              
              {/* Gallery Shortcode Display */}
              {((formData.gallery && formData.gallery.length > 0) || (existingGallery && existingGallery.length > 0)) && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gallery Shortcode (Copy and paste this into your content)
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      readOnly
                      value={`[gallery ids="GALLERY_PLACEHOLDER" style="${formData.gallery_style}"]`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white font-mono text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (existingShortcodes.length > 0) {
                          // If shortcodes exist, show message to remove them first
                          setCopyNotification('Remove existing shortcodes first!');
                          setTimeout(() => setCopyNotification(''), 2000);
                        } else if (!shortcodeCopied) {
                          // First click: Copy the shortcode
                          navigator.clipboard.writeText(`[gallery ids="GALLERY_PLACEHOLDER" style="${formData.gallery_style}"]`);
                          setShortcodeCopied(true);
                          setCopyNotification('Shortcode copied! Click again to insert.');
                          setTimeout(() => setCopyNotification(''), 2000);
                        } else {
                          // Second click: Insert the shortcode
                          const shortcode = `[gallery ids="GALLERY_PLACEHOLDER" style="${formData.gallery_style}"]`;
                          
                          // Get the Quill editor instance
                          const quillEditor = document.querySelector('.ql-editor');
                          if (quillEditor) {
                            // Focus the editor first
                            quillEditor.focus();
                            
                            // Get current selection
                            const selection = window.getSelection();
                            if (selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              // Insert the shortcode at cursor position
                              range.deleteContents();
                              range.insertNode(document.createTextNode(shortcode));
                              range.collapse(false);
                              selection.removeAllRanges();
                              selection.addRange(range);
                            } else {
                              // If no selection, append to end
                              quillEditor.innerHTML += shortcode;
                            }
                            
                            // Update the form data to reflect the change
                            setFormData(prev => ({
                              ...prev,
                              context: quillEditor.innerHTML
                            }));
                            
                            setCopyNotification('Gallery shortcode inserted!');
                            setTimeout(() => setCopyNotification(''), 2000);
                          }
                        }
                      }}
                      disabled={existingShortcodes.length > 0}
                      className={`px-4 py-2 rounded-md text-sm ${
                        existingShortcodes.length > 0 
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                          : shortcodeCopied
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {existingShortcodes.length > 0 
                        ? 'Remove existing shortcodes first' 
                        : shortcodeCopied 
                          ? 'Insert Gallery' 
                          : 'Copy Shortcode'
                      }
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Click "Copy Shortcode" to copy, then click "Insert Gallery" to add it to your content.
                  </p>
                  {copyNotification && (
                    <div className="mt-2 text-sm text-green-600 font-medium">
                      {copyNotification}
                    </div>
                  )}
                </div>
              )}

              {/* Existing Shortcodes Display */}
              {existingShortcodes.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <label className="block text-sm font-medium text-yellow-800 mb-2">
                    Existing Gallery Shortcodes ({existingShortcodes.length})
                  </label>
                  <p className="text-xs text-yellow-600 mb-3">
                    Remove existing shortcodes before inserting new ones.
                  </p>
                  {existingShortcodes.map((shortcode) => (
                    <div key={shortcode.id} className="flex items-center justify-between bg-white p-2 rounded border mb-2">
                      <code className="flex-1 text-sm text-gray-700 font-mono">
                        {shortcode.text}
                      </code>
                      <button
                        type="button"
                        onClick={() => removeShortcode(shortcode.text)}
                        className="ml-2 px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>


            {/* Upload Progress */}
            {isSubmitting && uploadProgress > 0 && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {uploadMessage || 'Uploading...'}
                  </span>
                  <span className="text-sm text-gray-500">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Error updating post
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      {errors.submit}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Fields Section */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Additional Information</h3>
                <button
                  type="button"
                  onClick={() => setShowAdditionalFields(!showAdditionalFields)}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {showAdditionalFields ? (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      Hide Additional Fields
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Show Additional Fields
                    </>
                  )}
                </button>
              </div>
              
              {showAdditionalFields && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Ad Title */}
                <div>
                  <label htmlFor="ad_title" className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    id="ad_title"
                    value={formData.ad_title}
                    onChange={(e) => handleInputChange('ad_title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter title"
                  />
                </div>

                {/* Ad Type */}
                <div>
                  <label htmlFor="ad_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <input
                    type="text"
                    id="ad_type"
                    value={formData.ad_type}
                    onChange={(e) => handleInputChange('ad_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter type"
                  />
                </div>

                {/* Ad URL */}
                <div>
                  <label htmlFor="ad_url" className="block text-sm font-medium text-gray-700 mb-2">
                    URL
                  </label>
                  <input
                    type="url"
                    id="ad_url"
                    value={formData.ad_url}
                    onChange={(e) => handleInputChange('ad_url', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="https://example.com"
                  />
                </div>

                {/* Ad Email */}
                <div>
                  <label htmlFor="ad_email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="ad_email"
                    value={formData.ad_email}
                    onChange={(e) => handleInputChange('ad_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="email@example.com"
                  />
                </div>

                {/* Ad Phone */}
                <div>
                  <label htmlFor="ad_phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="ad_phone"
                    name="ad_phone"
                    value={formData.ad_phone || ''}
                    onChange={(e) => handleInputChange('ad_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter phone number"
                  />
                </div>

                {/* Ad Date */}
                <div>
                  <label htmlFor="ad_date" className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    id="ad_date"
                    value={formData.ad_date && formData.ad_date !== '0000-00-00' ? formData.ad_date : ''}
                    onChange={(e) => handleInputChange('ad_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              )}

              {/* Comment Options Section */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Comment Settings</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="comments_closed_edit"
                      name="comment_status_edit"
                      value="closed"
                      checked={formData.comment_status === 'closed'}
                      onChange={(e) => handleInputChange('comment_status', e.target.value)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="comments_closed_edit" className="ml-3 block text-sm font-medium text-gray-700">
                      Comments Disabled (Default)
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="comments_open_edit"
                      name="comment_status_edit"
                      value="open"
                      checked={formData.comment_status === 'open'}
                      onChange={(e) => handleInputChange('comment_status', e.target.value)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="comments_open_edit" className="ml-3 block text-sm font-medium text-gray-700">
                      Comments Enabled
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-primary-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Updating...' : 'Update Post'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Unsaved Changes</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  You have unsaved changes. Are you sure you want to close? All changes will be lost.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={handleCancelModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Keep Editing
                </button>
                <button
                  onClick={handleConfirmModal}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successMessage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Success!</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostEditModal;
